import React from 'react';
import { ActivityResult } from '../../hooks/useVocabularyTraining';
import { Trophy, Zap, Target, CheckCircle, XCircle, BookOpen, Home, RotateCcw } from 'lucide-react';
import { StreakDisplay } from './StreakDisplay';

interface TrainingSummaryProps {
  results: ActivityResult[];
  onReview: () => void;
  onContinue: () => void;
  onGoToDashboard?: () => void;
  onGoToThemes?: () => void;
  onNewRound?: () => void;
  currentStreak: number;
  previousStreak: number;
  weekHistory: boolean[];
}

export const TrainingSummary: React.FC<TrainingSummaryProps> = ({ 
  results, 
  onReview, 
  onContinue,
  onGoToDashboard,
  onGoToThemes,
  onNewRound,
  currentStreak,
  previousStreak,
  weekHistory
}) => {
  const totalActivities = results.length;
  const correctCount = results.filter(r => r.success).length;
  const incorrectCount = totalActivities - correctCount;
  const accuracy = totalActivities > 0 ? Math.round((correctCount / totalActivities) * 100) : 0;
  const totalXP = correctCount * 10 + incorrectCount * 1; // 10 XP for correct, 1 XP for effort
  const isPerfect = incorrectCount === 0;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6 pb-24">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        {/* Celebration Header */}
        <div className="relative bg-gradient-to-br from-yellow-50 to-orange-50 p-8 text-center">
          {/* Decorative elements */}
          <div className="absolute top-4 left-4 w-16 h-16 bg-purple-200 rounded-full opacity-30 blur-xl"></div>
          <div className="absolute top-4 right-4 w-16 h-16 bg-blue-200 rounded-full opacity-30 blur-xl"></div>
          
          {/* Characters/Illustration area - simplified */}
          <div className="relative z-10 mb-4">
            <div className="flex justify-center items-center gap-4 mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center shadow-lg">
                <Trophy className="w-10 h-10 text-white" />
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>

          {/* Main Message */}
          <h1 className="text-3xl md:text-4xl font-bold text-yellow-600 mb-2 relative z-10">
            {isPerfect ? 'Perfect Lesson!' : 'Lesson Complete!'}
          </h1>
          <p className="text-gray-600 text-lg relative z-10">
            {isPerfect 
              ? "You didn't make any mistakes!" 
              : `You completed ${totalActivities} activities!`}
          </p>
        </div>

        {/* Stats Boxes */}
        <div className="p-6 grid grid-cols-2 gap-4">
          {/* Total XP */}
          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-4 border-2 border-yellow-200">
            <div className="text-yellow-700 text-xs font-bold uppercase mb-2">Total XP</div>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-600" />
              <span className="text-2xl font-bold text-yellow-700">{totalXP}</span>
            </div>
          </div>

          {/* Accuracy */}
          <div className={`bg-gradient-to-br rounded-xl p-4 border-2 ${
            isPerfect 
              ? 'from-green-50 to-emerald-50 border-green-200' 
              : accuracy >= 80
              ? 'from-blue-50 to-indigo-50 border-blue-200'
              : 'from-orange-50 to-red-50 border-orange-200'
          }`}>
            <div className={`text-xs font-bold uppercase mb-2 ${
              isPerfect 
                ? 'text-green-700' 
                : accuracy >= 80
                ? 'text-blue-700'
                : 'text-orange-700'
            }`}>
              {isPerfect ? 'Perfect' : accuracy >= 80 ? 'Great' : 'Good Try'}
            </div>
            <div className="flex items-center gap-2">
              <Target className={`w-5 h-5 ${
                isPerfect 
                  ? 'text-green-600' 
                  : accuracy >= 80
                  ? 'text-blue-600'
                  : 'text-orange-600'
              }`} />
              <span className={`text-2xl font-bold ${
                isPerfect 
                  ? 'text-green-700' 
                  : accuracy >= 80
                  ? 'text-blue-700'
                  : 'text-orange-700'
              }`}>
                {accuracy}%
              </span>
            </div>
          </div>
        </div>

        {/* Breakdown */}
        <div className="px-6 pb-4">
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-gray-700 font-medium">Correct</span>
              </div>
              <span className="text-gray-800 font-bold">{correctCount}</span>
            </div>
            {incorrectCount > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-600" />
                  <span className="text-gray-700 font-medium">Incorrect</span>
                </div>
                <span className="text-gray-800 font-bold">{incorrectCount}</span>
              </div>
            )}
          </div>
        </div>

        {/* Streak Display */}
        <div className="px-6 pb-4">
          <StreakDisplay
            currentStreak={currentStreak}
            previousStreak={previousStreak}
            weekHistory={weekHistory}
          />
        </div>

        {/* Action Buttons */}
        <div className="p-6 pt-4 space-y-3">
          {/* Review Button (if there are mistakes) */}
          {incorrectCount > 0 && (
            <button
              onClick={onReview}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
            >
              <BookOpen className="w-5 h-5" />
              Review Lesson
            </button>
          )}
          
          {/* Main Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            {onGoToThemes && (
              <button
                onClick={onGoToThemes}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
              >
                <BookOpen className="w-5 h-5" />
                Themes
              </button>
            )}
            {onGoToDashboard && (
              <button
                onClick={onGoToDashboard}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
              >
                <Home className="w-5 h-5" />
                Dashboard
              </button>
            )}
            {onNewRound && (
              <button
                onClick={onNewRound}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors"
              >
                <RotateCcw className="w-5 h-5" />
                New Round
              </button>
            )}
            {!onGoToDashboard && !onGoToThemes && !onNewRound && (
              <button
                onClick={onContinue}
                className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-colors ${
                  incorrectCount > 0
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700'
                }`}
              >
                Continue
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
